// ── Rendering Functions ──────────────────────────────────────────────
// SVG rendering for all canvas elements

import { state, clearSelection, saveState, isMultiSelected, type MultiSelectType } from './state.ts';

/** True if an element is either the single-selected target or in the multi-selection set. */
function isSel(type: MultiSelectType, id: number, selectedId: number | null): boolean {
  return selectedId === id || isMultiSelected(type, id);
}
import {
  $,
  metersToSvg, svgEl, clearLayer, ORIGIN,
  gridLayer, axisLayer, boundaryLayer, treeLayer,
  coverageLayer, heatmapLayer, zoneLayer, sprinklerLayer,
  labelLayer, guideLayer, smartGuideLayer, pipeLayer,
  dripLayer, valveObjLayer, weatherStationLayer, tempLayer,
  findSmartGuides,
  type SmartGuideExclusion,
} from './canvas.ts';
import {
  LANDSCAPE_OBJECTS,
  getPreset, getArcData, getAvailableArcs, getSeriesForModel, describeArc,
  calculatePipeLength as calcPipeLength,
  calculateDripEmitters, calculateDripFlow, checkDripLineLength,
  calculatePrecipitationHeatmap, getHeatmapLegend, heatmapLevel,
  getZoneStatus as logic_getZoneStatus,
  autoAssignSprinklersToZones as logic_autoAssign,
  WATER_SOURCE_DEFAULTS, validateWaterSourceCapacity,
} from '../calculations/logic.ts';
import {
  getSeries, getSprayBodies, getWeatherStations,
  getPresets, getValves, getDripTypes,
} from '../catalog/catalog-loader.ts';
import { t } from '../shared/i18n.ts';
import {
  getUnitLabels, convertFlow, convertLength, convertPrecipRate, toFieldText,
  formatLength, formatArea, formatFlow, formatFlowM3h,
  formatPressure, formatPrecipRate, formatSmallLength, formatRadius,
} from '../shared/units.ts';
import { escapeHtml, sanitizeColor } from '../shared/utils.ts';
import { showField } from './field-units.ts';

// ── Helper wrappers ────────────────────────────────────────────────
/** Each flyout list row leads with a <button class="list-item-select"> so it
 *  can be selected from the keyboard — the rows used to be click-only <div>s,
 *  and with the canvas taking no keyboard input that left no keyboard route
 *  to an element's properties. Lists are rebuilt on every render, which
 *  throws the focused row away: call this before clearing a list and the
 *  returned function afterwards to hand focus to the same row again, or to
 *  its neighbour if that row was just deleted. */
function keepRowFocus(list: HTMLElement): () => void {
  const active = document.activeElement;
  const rows = Array.from(list.children);
  const row = active ? rows.find((r) => r.contains(active)) : undefined;
  if (!row) return () => {};
  const index = rows.indexOf(row);
  const idAttr = Array.from(row.attributes).find((a) => /^data-.+-id$/.test(a.name));
  return () => {
    const next = Array.from(list.children) as HTMLElement[];
    const same = idAttr && next.find((r) => r.getAttribute(idAttr.name) === idAttr.value);
    const target = same || next[Math.min(index, next.length - 1)];
    const button = target?.matches(".list-item-select") ? target : target?.querySelector<HTMLElement>(".list-item-select");
    button?.focus();
  };
}

const currentAttr = (isCurrent: boolean) => (isCurrent ? ' aria-current="true"' : "");

export function autoAssignSprinklersToZones() {
  logic_autoAssign(state.sprinklers, state.zones);
}

export function getZoneSprinklers(zone: { id: number }) {
  return state.sprinklers.filter((sp) => sp.zoneId === zone.id);
}

export function getZoneStatus(zone: { id: number; name: string; points: { x: number; y: number }[]; valveModel?: string | null }) {
  return logic_getZoneStatus(zone, state.sprinklers);
}

// ── Build Selects ─────────────────────────────────────────────────
export function buildValveSelect(selectEl: HTMLSelectElement, selectedValve: string | null | undefined) {
  selectEl.innerHTML = `<option value="">${t("sidebar.noValve")}</option>`;
  for (const [key, valve] of Object.entries(getValves() || {})) {
    const opt = document.createElement("option");
    opt.value = key;
    opt.textContent = `${valve.name} (max ${formatFlow(Number(valve.maxFlow) || 0, state.userUnits)})`;
    if (key === selectedValve) opt.selected = true;
    selectEl.appendChild(opt);
  }
}

// The catalog's `description` is Ukrainian prose ("Антисифон 1\"") and was
// shown in every language. The type, size and flow control it restates are
// fields of their own, worded here in the user's language.
function valveSummary(valve: { type?: string | null; size?: string | null; flowControl?: boolean }): string {
  return [
    valve.type ? t(`productDetail.valveTypes.${valve.type}`) : null,
    valve.size,
    valve.flowControl ? t("productDetail.flowControl") : null,
  ].filter(Boolean).join(" · ");
}

export function buildValveModelSelect(selectEl: HTMLSelectElement, selectedModel: string) {
  selectEl.innerHTML = "";
  for (const [key, valve] of Object.entries(getValves() || {})) {
    const opt = document.createElement("option");
    opt.value = key;
    opt.textContent = `${valve.name} (${valveSummary(valve)})`;
    if (key === selectedModel) opt.selected = true;
    selectEl.appendChild(opt);
  }
}

export function buildZoneSelect(selectEl: HTMLSelectElement, selectedZoneId: number | null | undefined) {
  selectEl.innerHTML = `<option value="">${t("sidebar.noZone")}</option>`;
  state.zones.forEach((zone) => {
    const opt = document.createElement("option");
    opt.value = String(zone.id);
    opt.textContent = zone.name;
    if (zone.id === selectedZoneId) opt.selected = true;
    selectEl.appendChild(opt);
  });
}

export function buildModelSelect(selectEl: HTMLSelectElement, selectedModel: string) {
  selectEl.innerHTML = "";
  for (const series of (getSeries() || []) as Record<string, unknown>[]) {
    const models = Object.entries(getPresets() || {}).filter(([, p]) => p.series === series.key);
    if (models.length === 0) continue;
    const group = document.createElement("optgroup");
    group.label = formatSeriesGroupLabel(series);
    for (const [key, preset] of models) {
      const opt = document.createElement("option");
      opt.value = key;
      opt.textContent = `${key} (${describeThrow(preset)})`;
      if (key === selectedModel) opt.selected = true;
      group.appendChild(opt);
    }
    selectEl.appendChild(group);
  }
}

/**
 * The ground a nozzle model covers, in the user's units. The catalog's own
 * `description` is a metric string written in Ukrainian ("2.5–4.6 м",
 * "1.5×4.6 м лівий кут"), which the dropdown used to show in every language
 * and unit system.
 */
function describeThrow(preset: { shape?: string; width?: number; length?: number; radiusMin?: number; radiusMax?: number }): string {
  const unit = getUnitLabels(state.userUnits).length;
  const size = (m: number) => { const v = convertLength(m, state.userUnits); return v.toFixed(v < 1 ? 2 : 1); };
  if (preset.shape === "strip" && preset.width && preset.length) return `${size(preset.width)}×${size(preset.length)} ${unit}`;
  return `${size(preset.radiusMin ?? 0)}–${size(preset.radiusMax ?? 0)} ${unit}`;
}

/**
 * Build the optgroup label for a sprinkler-series group.
 *
 * Catalog data currently lacks display `name` and `pressure` for most series —
 * `db/catalogs/misc/series.json` only carries `models`, and DB rows have
 * those columns NULL. Without this helper, the dropdown rendered
 * "undefined (undefined bar)" between every group (issue #1371). Falls back
 * to a humanised series key (`mp-rotator` -> `MP Rotator`) and only appends
 * the pressure suffix when actually populated, so once the catalog seeder
 * enriches series with name/pressure this picks them up automatically.
 */
function formatSeriesGroupLabel(series: Record<string, unknown>): string {
  const labelRaw = series.label || series.name;
  const label =
    typeof labelRaw === "string" && labelRaw.trim().length > 0
      ? labelRaw
      : humaniseSeriesKey(typeof series.key === "string" ? series.key : "");
  const pressure =
    typeof series.pressure === "number" || typeof series.pressure === "string"
      ? String(series.pressure).trim()
      : "";
  if (pressure.length === 0) return label;
  const pressureNum = Number(pressure);
  return `${label} (${Number.isFinite(pressureNum) ? formatPressure(pressureNum, state.userUnits) : pressure + ' ' + t("units.bar")})`;
}

function humaniseSeriesKey(key: string): string {
  if (key.length === 0) return "—";
  return key
    .split("-")
    .map((word) => (word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(" ");
}

export function buildObjectTypeSelect(selectEl: HTMLSelectElement, selectedType: string) {
  selectEl.innerHTML = "";
  for (const [key, def] of Object.entries(LANDSCAPE_OBJECTS)) {
    const opt = document.createElement("option");
    opt.value = key;
    opt.textContent = `${def.icon} ${t(`objectTypes.${key}`) || def.label}`;
    if (key === selectedType) opt.selected = true;
    selectEl.appendChild(opt);
  }
}

export function buildDripModelSelect(selectEl: HTMLSelectElement, selectedModel: string) {
  selectEl.innerHTML = "";
  const brands = {};
  for (const [key, drip] of Object.entries(getDripTypes() || {})) {
    if (!(brands as Record<string, unknown[]>)[drip.brand]) (brands as Record<string, unknown[]>)[drip.brand] = [];
    (brands as Record<string, unknown[][]>)[drip.brand].push([key, drip]);
  }
  for (const [brand, models] of Object.entries(brands)) {
    const group = document.createElement("optgroup");
    group.label = brand;
    for (const [key, drip] of models as [string, Record<string, string>][]) {
      const opt = document.createElement("option");
      opt.value = key;
      opt.textContent = `${key} (${drip.description})`;
      if (key === selectedModel) opt.selected = true;
      group.appendChild(opt);
    }
    selectEl.appendChild(group);
  }
}

export function buildBodySelect(selectEl: HTMLSelectElement, nozzleModel: string, selectedBody: string) {
  selectEl.innerHTML = "";
  const noBodyOpt = document.createElement("option");
  noBodyOpt.value = "";
  noBodyOpt.textContent = t("sidebar.noBody");
  selectEl.appendChild(noBodyOpt);
  const preset = ((getPresets() || {}) as Record<string, Record<string, string>>)[nozzleModel];
  const series = preset ? preset.series : "";
  const nozzleBrand = series.startsWith("rb-") ? "Rain Bird"
    : series.startsWith("gardena") ? "Gardena"
    : series.startsWith("toro") ? "Toro" : "Hunter";
  for (const [key, body] of Object.entries(getSprayBodies() || {})) {
    if (body.brand !== nozzleBrand) continue;
    const opt = document.createElement("option");
    opt.value = key;
    opt.textContent = `${key} (${body.riser})`;
    if (key === selectedBody) opt.selected = true;
    selectEl.appendChild(opt);
  }
}

export function populateArcSelect(selectEl: HTMLSelectElement, model: string, currentArc: number) {
  const arcs = getAvailableArcs(model);
  selectEl.replaceChildren();
  for (const a of arcs) {
    const opt = document.createElement("option");
    opt.value = String(a);
    opt.textContent = `${a}°`;
    if (a === currentArc) opt.selected = true;
    selectEl.appendChild(opt);
  }
}

export function buildWeatherStationModelSelect(selectEl: HTMLSelectElement, selectedModel: string) {
  selectEl.innerHTML = "";
  for (const station of (getWeatherStations() || []) as Record<string, string>[]) {
    const opt = document.createElement("option");
    opt.value = station.id;
    opt.textContent = `${station.name} (${station.brand})`;
    if (station.id === selectedModel) opt.selected = true;
    selectEl.appendChild(opt);
  }
}

// ── Grid ──────────────────────────────────────────────────────────
export function renderGrid() {
  clearLayer(gridLayer()); clearLayer(axisLayer());
  if (!state.showGrid) return;
  const g = state.gridSize, ppm = state.pxPerMeter, w = state.plotWidth, h = state.plotHeight, margin = 2;
  const isMajor = (v: number) => Math.abs(Math.round(v) - v) < 0.001 && Math.round(v) % 5 === 0;
  for (let mx = -margin; mx <= w + margin; mx += g) {
    const sx = ORIGIN.x + mx * ppm, major = isMajor(mx);
    gridLayer().appendChild(svgEl("line", { x1: sx, y1: ORIGIN.y - margin * ppm, x2: sx, y2: ORIGIN.y + (h + margin) * ppm, stroke: major ? "#ccc" : "#e5e5e5", "stroke-width": major ? 0.8 : 0.4 }));
  }
  for (let my = -margin; my <= h + margin; my += g) {
    const sy = ORIGIN.y + my * ppm, major = isMajor(my);
    gridLayer().appendChild(svgEl("line", { x1: ORIGIN.x - margin * ppm, y1: sy, x2: ORIGIN.x + (w + margin) * ppm, y2: sy, stroke: major ? "#ccc" : "#e5e5e5", "stroke-width": major ? 0.8 : 0.4 }));
  }
  axisLayer().appendChild(svgEl("line", { x1: ORIGIN.x - margin * ppm, y1: ORIGIN.y, x2: ORIGIN.x + (w + margin) * ppm, y2: ORIGIN.y, stroke: "#999", "stroke-width": 1.5 }));
  axisLayer().appendChild(svgEl("line", { x1: ORIGIN.x, y1: ORIGIN.y - margin * ppm, x2: ORIGIN.x, y2: ORIGIN.y + (h + margin) * ppm, stroke: "#999", "stroke-width": 1.5 }));
  const step = g >= 2 ? g : (w > 30 ? 5 : (g < 1 ? 1 : g));
  for (let mx = 0; mx <= w; mx += step) {
    const label = svgEl("text", { x: ORIGIN.x + mx * ppm, y: ORIGIN.y - 8, class: "axis-label", "text-anchor": "middle" });
    label.textContent = String(mx); axisLayer().appendChild(label);
  }
  for (let my = step; my <= h; my += step) {
    const label = svgEl("text", { x: ORIGIN.x - 8, y: ORIGIN.y + my * ppm + 4, class: "axis-label", "text-anchor": "end" });
    label.textContent = String(my); axisLayer().appendChild(label);
  }
  gridLayer().appendChild(svgEl("rect", { x: ORIGIN.x, y: ORIGIN.y, width: w * ppm, height: h * ppm, fill: "rgba(255,255,255,0.5)", stroke: "#aaa", "stroke-width": 1, "stroke-dasharray": "4 2" }));
}

// ── Boundary ──────────────────────────────────────────────────────
export function renderBoundary() {
  clearLayer(boundaryLayer());
  if (state.boundary.length < 2) return;
  const points = state.boundary.map((p) => { const s = metersToSvg(p.x, p.y); return `${s.x},${s.y}`; }).join(" ");
  if (state.boundary.length >= 3) {
    boundaryLayer().appendChild(svgEl("polygon", { points, class: "boundary-polygon" }));
  } else {
    boundaryLayer().appendChild(svgEl("polyline", { points, fill: "none", stroke: "#2e7d32", "stroke-width": 2 }));
  }
  state.boundary.forEach((p, i) => {
    const s = metersToSvg(p.x, p.y);
    boundaryLayer().appendChild(svgEl("circle", { cx: s.x, cy: s.y, r: 5, class: "boundary-vertex", "data-type": "boundary-vertex", "data-index": i }));
  });
}

// ── Coverage ──────────────────────────────────────────────────────
export function renderCoverage() {
  clearLayer(coverageLayer());
  // Rings render only in 'rings' mode; 'heatmap' takes over the same
  // visual slot (see renderHeatmap) and 'off' hides the layer entirely.
  if (state.coverageView !== 'rings') return;
  state.sprinklers.forEach((sp) => {
    const center = metersToSvg(sp.x, sp.y);
    const ppm = state.pxPerMeter;
    const fillColor = "#29b6f618", strokeColor = "#29b6f660";
    const preset = getPreset(sp.model);
    if (preset.shape === "strip") {
      const wPx = preset.width! * ppm, lPx = preset.length! * ppm, rot = sp.rotation || 0;
      let ox = 0, oy = 0;
      const pos = preset.position || "side";
      if (pos === "side") { ox = -lPx / 2; oy = -wPx; }
      else if (pos === "center") { ox = -lPx / 2; oy = -wPx / 2; }
      else if (pos === "left-corner") { ox = 0; oy = -wPx; }
      else if (pos === "right-corner") { ox = -lPx; oy = -wPx; }
      else if (pos === "end") { ox = -lPx / 2; oy = -wPx; }
      coverageLayer().appendChild(svgEl("rect", { x: center.x + ox, y: center.y + oy, width: lPx, height: wPx, fill: fillColor, stroke: strokeColor, "stroke-width": 1, "stroke-dasharray": "4 2", "pointer-events": "none", transform: `rotate(${rot} ${center.x} ${center.y})` }));
    } else if (sp.arc === 360) {
      coverageLayer().appendChild(svgEl("circle", { cx: center.x, cy: center.y, r: sp.radius! * ppm, fill: fillColor, stroke: strokeColor, "stroke-width": 1, "stroke-dasharray": "4 2", "pointer-events": "none" }));
    } else {
      const rotation = sp.rotation || 0, halfArc = sp.arc / 2;
      const d = describeArc(center.x, center.y, sp.radius! * ppm, rotation - halfArc - 90, rotation + halfArc - 90);
      coverageLayer().appendChild(svgEl("path", { d, fill: fillColor, stroke: strokeColor, "stroke-width": 1, "stroke-dasharray": "4 2", "pointer-events": "none" }));
    }
  });
}

// ── Heatmap ──────────────────────────────────────────────────────
let _heatmapCache: unknown = null, _heatmapKey = "";
function getHeatmapCacheKey() {
  return JSON.stringify({ s: state.sprinklers.map(sp => ({ x: sp.x, y: sp.y, r: sp.radius, a: sp.arc, m: sp.model, rot: sp.rotation })), b: state.boundary, pw: state.plotWidth, ph: state.plotHeight });
}

export function renderHeatmap() {
  clearLayer(heatmapLayer());
  if (state.coverageView !== 'heatmap' || !state.sprinklers || state.sprinklers.length === 0) return;
  const resolution = 0.15, key = getHeatmapCacheKey();
  let data;
  if (_heatmapCache && _heatmapKey === key) { data = _heatmapCache; }
  else { data = calculatePrecipitationHeatmap({ sprinklers: state.sprinklers, boundary: state.boundary, plotWidth: state.plotWidth, plotHeight: state.plotHeight, resolution }); _heatmapCache = data; _heatmapKey = key; }
  const grid = (data as Record<string, number[][]>).grid;
  const cols = grid[0] ? grid[0].length : 0, rows = grid.length;
  if (cols === 0 || rows === 0) return;
  const canvas = document.createElement("canvas"); canvas.width = cols; canvas.height = rows;
  const ctx = canvas.getContext("2d");
  const colorMap = [[0,0,0,0],[173,216,230,100],[0,150,255,130],[0,100,200,160],[0,50,180,190],[180,0,0,200]];
  const imgData = ctx!.createImageData(cols, rows);
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const ci = heatmapLevel(grid[row][col]);
      const px = (row * cols + col) * 4;
      imgData.data[px] = colorMap[ci][0]; imgData.data[px+1] = colorMap[ci][1]; imgData.data[px+2] = colorMap[ci][2]; imgData.data[px+3] = colorMap[ci][3];
    }
  }
  ctx!.putImageData(imgData, 0, 0);
  const blurCanvas = document.createElement("canvas"); blurCanvas.width = cols; blurCanvas.height = rows;
  const bCtx = blurCanvas.getContext("2d"); bCtx!.filter = "blur(2px)"; bCtx!.drawImage(canvas, 0, 0);
  const ppm = state.pxPerMeter, widthPx = cols * resolution * ppm, heightPx = rows * resolution * ppm, origin = metersToSvg(0, 0);
  heatmapLayer().appendChild(svgEl("image", { href: blurCanvas.toDataURL(), x: origin.x, y: origin.y, width: widthPx, height: heightPx, preserveAspectRatio: "none", "image-rendering": "auto", opacity: "0.85", class: "heatmap-cell" }));
  renderHeatmapLegend();
}

/** A heatmap band edge (mm/h) as a bare number in the user's units. */
function bandEdge(mmh: number): string {
  return state.userUnits === "imperial" ? convertPrecipRate(mmh, "imperial").toFixed(2) : String(mmh);
}

export function renderHeatmapLegend() {
  const panel = $("#panelHeatmapLegend"), legendContainer = $("#heatmapLegend");
  if (!panel || !legendContainer) return;
  if (state.coverageView !== 'heatmap') { panel.hidden = true; return; }
  panel.hidden = false; legendContainer.innerHTML = "";
  const precipUnit = getUnitLabels(state.userUnits).precipRate;
  getHeatmapLegend().forEach((entry) => {
    const item = document.createElement("div"); item.className = "heatmap-legend-item";
    // dynamic: swatch color is data-driven (heatmap legend palette); the "no coverage" bucket is static, so CSS owns it
    const isEmpty = entry.color === "transparent";
    const swatch = document.createElement("span"); swatch.className = isEmpty ? "heatmap-legend-swatch heatmap-legend-swatch--empty" : "heatmap-legend-swatch"; if (!isEmpty) swatch.style.backgroundColor = entry.color;
    const label = document.createElement("span"); label.textContent = t(entry.labelKey, { unit: precipUnit, min: bandEdge(entry.min ?? 0), max: bandEdge(entry.max ?? 0) });
    item.appendChild(swatch); item.appendChild(label); legendContainer.appendChild(item);
  });
}

// ── Zones ─────────────────────────────────────────────────────────
export function renderZones() {
  clearLayer(zoneLayer());
  state.zones.forEach((zone) => {
    if (zone.points.length < 3) return;
    const points = zone.points.map((p) => { const s = metersToSvg(p.x, p.y); return `${s.x},${s.y}`; }).join(" ");
    const status = getZoneStatus(zone), isSelected = isSel('zone', zone.id, state.selectedZone);
    const colors: Record<string, { fill: string; stroke: string }> = { "ok": { fill: "rgba(76,175,80,0.12)", stroke: "#4caf50" }, "warning": { fill: "rgba(255,152,0,0.15)", stroke: "#ff9800" }, "over-capacity": { fill: "rgba(211,47,47,0.15)", stroke: "#d32f2f" }, "no-valve": { fill: zone.color || "rgba(156,39,176,0.1)", stroke: "#9c27b0" } };
    const c = colors[status.status] || colors["no-valve"];
    zoneLayer().appendChild(svgEl("polygon", { points, class: "zone-polygon", "data-zone-id": zone.id, fill: c.fill, stroke: c.stroke, "stroke-width": isSelected ? 3 : 1.5, "stroke-dasharray": isSelected ? "none" : "6 3", cursor: "pointer" }));
    const cx = zone.points.reduce((s, p) => s + p.x, 0) / zone.points.length;
    const cy = zone.points.reduce((s, p) => s + p.y, 0) / zone.points.length;
    const labelPos = metersToSvg(cx, cy);
    const lbl = svgEl("text", { x: labelPos.x, y: labelPos.y, class: "sprinkler-label", fill: c.stroke, "text-anchor": "middle", "pointer-events": "none" });
    lbl.textContent = `${zone.name} (${formatFlow(status.flow, state.userUnits)})`; zoneLayer().appendChild(lbl);
    zone.points.forEach((p, i) => { const s = metersToSvg(p.x, p.y); zoneLayer().appendChild(svgEl("circle", { cx: s.x, cy: s.y, r: 4, class: "zone-vertex", "data-type": "zone-vertex", "data-zone-id": zone.id, "data-index": i })); });
  });
}

// ── Pipes ─────────────────────────────────────────────────────────
export function renderPipes() {
  clearLayer(pipeLayer());
  state.pipes.forEach((pipe) => {
    if (pipe.points.length < 2) return;
    const pts = pipe.points.map((p) => { const s = metersToSvg(p.x, p.y); return `${s.x},${s.y}`; }).join(" ");
    const isSelected = isSel('pipe', pipe.id, state.selectedPipe), strokeW = Math.max(2, pipe.diameter / 12);
    pipeLayer().appendChild(svgEl("polyline", { points: pts, class: `pipe-polyline${isSelected ? " selected" : ""}`, "stroke-width": isSelected ? strokeW + 2 : strokeW, "data-type": "pipe", "data-id": pipe.id }));
    pipe.points.forEach((p, i) => { const s = metersToSvg(p.x, p.y); pipeLayer().appendChild(svgEl("circle", { cx: s.x, cy: s.y, r: 3, class: "pipe-vertex", "data-type": "pipe-vertex", "data-pipe-id": pipe.id, "data-index": i })); });
    const mid = Math.floor(pipe.points.length / 2), p0 = pipe.points[mid - 1] || pipe.points[0], p1 = pipe.points[mid];
    const mx = (p0.x + p1.x) / 2, my = (p0.y + p1.y) / 2, labelPos = metersToSvg(mx, my);
    const lbl = svgEl("text", { x: labelPos.x, y: labelPos.y - 6, class: "pipe-label" }); lbl.textContent = `${pipe.name} \u2205${formatSmallLength(pipe.diameter, state.userUnits)}`; pipeLayer().appendChild(lbl);
  });
}

let _pipeListDelegated = false;
export function renderPipeList(renderAll: () => void) {
  const list = $("#pipeList"); const refocus = keepRowFocus(list); list.innerHTML = "";
  if (state.pipes.length === 0) { list.innerHTML = `<div class="empty-state">${t("emptyStates.pipes")}</div>`; }
  state.pipes.forEach((pipe) => {
    const lenLabel = formatLength(calcPipeLength(pipe), state.userUnits);
    const diameterLabel = formatSmallLength(pipe.diameter, state.userUnits);
    const item = document.createElement("div");
    item.className = `pipe-item${state.selectedPipe === pipe.id ? " selected" : ""}`;
    item.innerHTML = `<button type="button" class="list-item-select"${currentAttr(state.selectedPipe === pipe.id)}><span class="name">${escapeHtml(pipe.name)}</span><span class="coords">\u2205${diameterLabel} ${lenLabel}</span></button><button class="delete-btn" data-delete-pipe="${escapeHtml(String(pipe.id))}" title="${t("sidebar.deleteBtn")}">&times;</button>`;
    item.dataset.pipeId = String(pipe.id); list.appendChild(item);
  });
  refocus();
  if (!_pipeListDelegated) { _pipeListDelegated = true;
    list.addEventListener("click", (e: MouseEvent) => { const deleteBtn = (e.target as HTMLElement).closest(".delete-btn") as HTMLElement; if (deleteBtn) { const id = parseInt(deleteBtn.dataset.deletePipe!); saveState(); state.pipes = state.pipes.filter((p) => p.id !== id); if (state.selectedPipe === id) state.selectedPipe = null; renderAll(); return; } const item = (e.target as HTMLElement).closest(".pipe-item") as HTMLElement; if (item) { clearSelection(); state.selectedPipe = parseInt(item.dataset.pipeId!); renderAll(); } }); }
  $("#pipeCount").textContent = `(${state.pipes.length})`; $("#panelPipeList").hidden = state.pipes.length === 0 && !state.selectedPipe;
}

export function updatePipePropertiesPanel() {
  const panel = $("#panelPipeProps"), pipe = state.pipes.find((p) => p.id === state.selectedPipe);
  if (!pipe) { panel.hidden = true; return; }
  panel.hidden = false; $("#pipeName").value = pipe.name; $("#pipeDiameter").value = pipe.diameter;
  buildZoneSelect($("#pipeZone"), pipe.zoneId); $("#pipeLength").textContent = formatLength(calcPipeLength(pipe), state.userUnits);
}

// ── Drip Lines ────────────────────────────────────────────────────
export function renderDripLines() {
  clearLayer(dripLayer());
  state.dripLines.forEach((dl) => {
    if (dl.points.length < 2) return;
    const type = ((getDripTypes() || {}) as Record<string, Record<string, unknown>>)[dl.model] || {}, color = (type.color as string) || "#795548";
    const pts = dl.points.map((p) => { const s = metersToSvg(p.x, p.y); return `${s.x},${s.y}`; }).join(" ");
    const isSelected = isSel('dripLine', dl.id, state.selectedDripLine);
    const wettingWidthPx = 0.3 * 2 * state.pxPerMeter;
    dripLayer().appendChild(svgEl("polyline", { points: pts, fill: "none", stroke: color, "stroke-width": wettingWidthPx, "stroke-opacity": 0.15, "stroke-linecap": "round", "stroke-linejoin": "round" }));
    dripLayer().appendChild(svgEl("polyline", { points: pts, fill: "none", stroke: color, "stroke-width": isSelected ? 4 : 2.5, "stroke-dasharray": "6 4", "data-type": "dripLine", "data-id": dl.id }));
    const emitterSpacing = type.emitterSpacing as number | undefined;
    if (emitterSpacing && emitterSpacing > 0) {
      const spacingM = emitterSpacing / 100; let accumulated = 0;
      for (let i = 1; i < dl.points.length; i++) {
        const dx = dl.points[i].x - dl.points[i-1].x, dy = dl.points[i].y - dl.points[i-1].y, segLen = Math.sqrt(dx*dx + dy*dy);
        if (segLen === 0) continue; const ux = dx / segLen, uy = dy / segLen; let pos = spacingM - accumulated;
        while (pos <= segLen) { const ex = dl.points[i-1].x + ux * pos, ey = dl.points[i-1].y + uy * pos, es = metersToSvg(ex, ey);
          dripLayer().appendChild(svgEl("circle", { cx: es.x, cy: es.y, r: 2.5, fill: color, stroke: "#fff", "stroke-width": 0.5 })); pos += spacingM; }
        accumulated = pos - segLen;
      }
    }
    dl.points.forEach((p, i) => { const s = metersToSvg(p.x, p.y); dripLayer().appendChild(svgEl("circle", { cx: s.x, cy: s.y, r: 3, class: "pipe-vertex", "data-type": "dripLine-vertex", "data-drip-id": dl.id, "data-index": i })); });
    const mid = Math.floor(dl.points.length / 2), p0 = dl.points[mid-1] || dl.points[0], p1 = dl.points[mid];
    const mx = (p0.x + p1.x) / 2, my = (p0.y + p1.y) / 2, labelPos = metersToSvg(mx, my);
    const lbl = svgEl("text", { x: labelPos.x, y: labelPos.y - 6, class: "pipe-label" }); lbl.textContent = dl.name ?? ""; dripLayer().appendChild(lbl);
  });
}

let _dripListDelegated = false;
export function renderDripList(renderAll: () => void) {
  const list = $("#dripList"); const refocus = keepRowFocus(list); list.innerHTML = "";
  if (state.dripLines.length === 0) { list.innerHTML = `<div class="empty-state">${t("emptyStates.dripLines")}</div>`; }
  state.dripLines.forEach((dl) => {
    const lenLabel = formatLength(calcPipeLength(dl), state.userUnits);
    const emitters = calculateDripEmitters({ dripLine: dl });
    const item = document.createElement("div");
    item.className = `pipe-item${state.selectedDripLine === dl.id ? " selected" : ""}`;
    item.innerHTML = `<button type="button" class="list-item-select"${currentAttr(state.selectedDripLine === dl.id)}><span class="name">${escapeHtml(dl.name)}</span><span class="coords">${escapeHtml(dl.model)} ${lenLabel} (${emitters} ${t("units.em")})</span></button><button class="delete-btn" data-delete-drip="${escapeHtml(String(dl.id))}" title="${t("sidebar.deleteBtn")}">&times;</button>`;
    item.dataset.dripId = String(dl.id); list.appendChild(item);
  });
  refocus();
  if (!_dripListDelegated) { _dripListDelegated = true;
    list.addEventListener("click", (e: MouseEvent) => { const deleteBtn = (e.target as HTMLElement).closest(".delete-btn") as HTMLElement; if (deleteBtn) { const id = parseInt(deleteBtn.dataset.deleteDrip!); saveState(); state.dripLines = state.dripLines.filter((dl) => dl.id !== id); if (state.selectedDripLine === id) state.selectedDripLine = null; renderAll(); return; } const item = (e.target as HTMLElement).closest(".pipe-item") as HTMLElement; if (item && item.dataset.dripId) { clearSelection(); state.selectedDripLine = parseInt(item.dataset.dripId); renderAll(); } }); }
  $("#dripCount").textContent = `(${state.dripLines.length})`; $("#panelDripList").hidden = state.dripLines.length === 0 && !state.selectedDripLine;
}

export function updateDripPropertiesPanel() {
  const panel = $("#panelDripProps"), dl = state.dripLines.find((d) => d.id === state.selectedDripLine);
  if (!dl) { panel.hidden = true; return; }
  panel.hidden = false; $("#dripLineName").value = dl.name; buildDripModelSelect($("#dripLineModel"), dl.model); buildZoneSelect($("#dripLineZone"), dl.zoneId);
  $("#dripLineLength").textContent = formatLength(calcPipeLength(dl), state.userUnits);
  $("#dripLineEmitters").textContent = calculateDripEmitters({ dripLine: dl });
  // Litres to gallons is the same factor per hour as per minute.
  const dripLph = calculateDripFlow({ dripLine: dl });
  $("#dripLineFlow").textContent = state.userUnits === "imperial"
    ? `${convertFlow(dripLph, "imperial").toFixed(2)} ${t("units.gph")}`
    : `${dripLph.toFixed(1)} ${t("units.lph")}`;
  const check = checkDripLineLength({ dripLine: dl }), warning = $("#dripWarning");
  if (!check.ok) { warning.hidden = false; warning.textContent = `${t("sidebar.dripWarning")} (${formatLength(check.length ?? 0, state.userUnits)} / ${formatLength(check.maxLength ?? 0, state.userUnits)})`; warning.className = "drip-warning zone-warning"; }
  else { warning.hidden = true; }
}

// ── Trees ─────────────────────────────────────────────────────────
export function renderTrees() {
  clearLayer(treeLayer());
  state.trees.forEach((tree) => {
    const pos = metersToSvg(tree.x, tree.y), def = LANDSCAPE_OBJECTS[tree.type];
    const shape = (def && def.shape) || tree.shape || 'circle', isSelected = isSel('tree', tree.id, state.selectedTree);
    if (shape === 'rect') { const wPx = (tree.width || 1) * state.pxPerMeter, hPx = (tree.length || 1) * state.pxPerMeter;
      treeLayer().appendChild(svgEl("rect", { x: pos.x - wPx/2, y: pos.y - hPx/2, width: wPx, height: hPx, transform: `rotate(${tree.rotation || 0} ${pos.x} ${pos.y})`, class: `tree-crown ${tree.type}${isSelected ? " selected" : ""}`, "data-type": "tree", "data-id": tree.id }));
    } else { treeLayer().appendChild(svgEl("circle", { cx: pos.x, cy: pos.y, r: tree.radius! * state.pxPerMeter, class: `tree-crown ${tree.type}${isSelected ? " selected" : ""}`, "data-type": "tree", "data-id": tree.id })); }
    const lbl = svgEl("text", { x: pos.x, y: pos.y + 4, class: "tree-label" }); lbl.textContent = tree.name ?? ""; treeLayer().appendChild(lbl);
  });
}

let _treeListDelegated = false;
export function renderTreeList({ selectTree, renderAll }: { selectTree: (id: number) => void; renderAll: () => void }) {
  const list = $("#treeList"); const refocus = keepRowFocus(list); list.innerHTML = "";
  if (state.trees.length === 0) { list.innerHTML = `<div class="empty-state">${t("emptyStates.trees")}</div>`; }
  state.trees.forEach((tree) => {
    const def = LANDSCAPE_OBJECTS[tree.type], typeLabel = t(`objectTypes.${tree.type}`) || (def && def.label) || tree.type;
    const icon = def ? def.icon : '', shape = (def && def.shape) || tree.shape || 'circle';
    const sizeInfo = shape === 'rect'
      ? `${formatLength(tree.width || 1, state.userUnits)} × ${formatLength(tree.length || 1, state.userUnits)}`
      : `r=${formatRadius(tree.radius || 0, state.userUnits)}`;
    const item = document.createElement("div"); item.className = `tree-item${state.selectedTree === tree.id ? " selected" : ""}`;
    item.innerHTML = `<button type="button" class="list-item-select"${currentAttr(state.selectedTree === tree.id)}><span class="name">${icon} ${escapeHtml(tree.name)}</span><span class="coords">${escapeHtml(typeLabel)} ${sizeInfo}</span></button><button class="delete-btn" data-delete-tree="${escapeHtml(String(tree.id))}" title="${t("sidebar.deleteBtn")}">&times;</button>`;
    item.dataset.treeId = String(tree.id); list.appendChild(item);
  });
  refocus();
  if (!_treeListDelegated) { _treeListDelegated = true;
    list.addEventListener("click", (e: MouseEvent) => { const deleteBtn = (e.target as HTMLElement).closest(".delete-btn") as HTMLElement; if (deleteBtn) { const id = parseInt(deleteBtn.dataset.deleteTree!); saveState(); state.trees = state.trees.filter((t) => t.id !== id); if (state.selectedTree === id) state.selectedTree = null; renderAll(); return; } const item = (e.target as HTMLElement).closest(".tree-item") as HTMLElement; if (item) selectTree(parseInt(item.dataset.treeId!)); }); }
  $("#treeCount").textContent = `(${state.trees.length})`; $("#panelTreeList").hidden = state.trees.length === 0 && !state.selectedTree;
}

export function updateTreePropertiesPanel() {
  const panel = $("#panelTreeProps"), tree = state.trees.find((t) => t.id === state.selectedTree);
  if (!tree) { panel.hidden = true; return; }
  panel.hidden = false; $("#treeName").value = tree.name; $("#treeType").value = tree.type; showField($("#treeX"), tree.x, "length", 1); showField($("#treeY"), tree.y, "length", 1);
  const def = LANDSCAPE_OBJECTS[tree.type], shape = (def && def.shape) || tree.shape || 'circle', isRect = shape === 'rect';
  $("#panelCircleProps").hidden = isRect; const rectPanel = $("#panelRectProps"); if (rectPanel) rectPanel.hidden = !isRect;
  if (isRect) { showField($("#objWidth"), tree.width || 1, "length"); showField($("#objLength"), tree.length || 1, "length"); $("#objRotation").value = tree.rotation || 0; }
  else { showField($("#treeRadius"), tree.radius ?? 0, "length"); }

  // Show/hide water source panel based on tree type
  const wsPanel = $("#panelWaterSourceProps");
  if (wsPanel) {
    if (tree.type === "waterSource") {
      wsPanel.hidden = false;
      const props = tree.waterSourceProps || { ...WATER_SOURCE_DEFAULTS };
      showField($("#wsFlowRate"), props.flow_rate, "flow");
      showField($("#wsPressure"), props.pressure, "pressure");
      $("#wsSourceType").value = props.source_type;
      showField($("#wsElevation"), props.elevation, "length");
      const capacityCheck = validateWaterSourceCapacity({ waterSource: props, sprinklers: state.sprinklers });
      const wsWarning = $("#wsCapacityWarning");
      if (wsWarning) {
        if (!capacityCheck.sufficient && capacityCheck.totalDemand > 0) {
          wsWarning.hidden = false;
          wsWarning.textContent = `${t("sidebar.wsCapacityExceeded")} (${formatFlow(capacityCheck.totalDemand, state.userUnits)} / ${formatFlow(capacityCheck.sourceCapacity, state.userUnits)})`;
          wsWarning.className = "zone-warning over-capacity";
        } else if (capacityCheck.utilizationPercent > 85) {
          wsWarning.hidden = false;
          wsWarning.textContent = `${t("sidebar.wsCapacityWarning")} (${capacityCheck.utilizationPercent}%)`;
          wsWarning.className = "zone-warning warning";
        } else {
          wsWarning.hidden = true;
        }
      }
    } else {
      wsPanel.hidden = true;
    }
  }
}

// ── Valve Objects ─────────────────────────────────────────────────
export function renderValveObjects() {
  clearLayer(valveObjLayer());
  state.valveObjects.forEach((vo) => {
    const pos = metersToSvg(vo.x, vo.y), isSelected = isSel('valveObj', vo.id, state.selectedValveObj), size = 8;
    const g = svgEl("g", { class: `valve-obj-marker${isSelected ? " selected" : ""}`, "data-type": "valveObj", "data-id": vo.id, transform: `translate(${pos.x}, ${pos.y})` });
    g.appendChild(svgEl("polygon", { points: `0,${-size} ${size},0 0,${size} ${-size},0`, class: "body" }));
    valveObjLayer().appendChild(g);
    const lbl = svgEl("text", { x: pos.x, y: pos.y + size + 12, class: "valve-obj-label" }); lbl.textContent = vo.name ?? ""; valveObjLayer().appendChild(lbl);
  });
}

let _valveObjListDelegated = false;
export function renderValveObjList({ selectValveObj, renderAll }: { selectValveObj: (id: number) => void; renderAll: () => void }) {
  const list = $("#valveObjList"); const refocus = keepRowFocus(list); list.innerHTML = "";
  if (state.valveObjects.length === 0) { list.innerHTML = `<div class="empty-state">${t("emptyStates.valves")}</div>`; }
  state.valveObjects.forEach((vo) => {
    const valve = ((getValves() || {}) as Record<string, Record<string, string>>)[vo.model], zoneName = vo.zoneId ? (state.zones.find((z) => z.id === vo.zoneId)?.name || "") : "";
    const item = document.createElement("div"); item.className = `valve-obj-item${state.selectedValveObj === vo.id ? " selected" : ""}`;
    item.innerHTML = `<button type="button" class="list-item-select"${currentAttr(state.selectedValveObj === vo.id)}><span class="name">${escapeHtml(vo.name)}</span><span class="coords">${valve ? valve.size : escapeHtml(vo.model)}${zoneName ? " | " + escapeHtml(zoneName) : ""}</span></button><button class="delete-btn" data-delete-valve-obj="${escapeHtml(String(vo.id))}" title="${t("sidebar.deleteBtn")}">&times;</button>`;
    item.dataset.valveObjId = String(vo.id); list.appendChild(item);
  });
  refocus();
  if (!_valveObjListDelegated) { _valveObjListDelegated = true;
    list.addEventListener("click", (e: MouseEvent) => { const deleteBtn = (e.target as HTMLElement).closest(".delete-btn") as HTMLElement; if (deleteBtn) { const id = parseInt(deleteBtn.dataset.deleteValveObj!); saveState(); state.valveObjects = state.valveObjects.filter((v) => v.id !== id); if (state.selectedValveObj === id) state.selectedValveObj = null; renderAll(); return; } const item = (e.target as HTMLElement).closest(".valve-obj-item") as HTMLElement; if (item) selectValveObj(parseInt(item.dataset.valveObjId!)); }); }
  $("#valveObjCount").textContent = `(${state.valveObjects.length})`; $("#panelValveObjList").hidden = state.valveObjects.length === 0 && !state.selectedValveObj;
}

export function updateValveObjPropertiesPanel() {
  const panel = $("#panelValveObjProps"), vo = state.valveObjects.find((v) => v.id === state.selectedValveObj);
  if (!vo) { panel.hidden = true; return; }
  panel.hidden = false; $("#valveObjName").value = vo.name; showField($("#valveObjX"), vo.x, "length", 1); showField($("#valveObjY"), vo.y, "length", 1);
  buildValveModelSelect($("#valveObjModel"), vo.model); buildZoneSelect($("#valveObjZone"), vo.zoneId);
}

// ── Weather Stations ─────────────────────────────────────────────
export function renderWeatherStations() {
  clearLayer(weatherStationLayer());
  state.weatherStations.forEach((ws) => {
    const pos = metersToSvg(ws.x, ws.y), isSelected = isSel('weatherStation', ws.id, state.selectedWeatherStation), size = 10;
    const g = svgEl("g", { class: `weather-station-marker${isSelected ? " selected" : ""}`, "data-type": "weatherStation", "data-id": ws.id, transform: `translate(${pos.x}, ${pos.y})` });
    g.appendChild(svgEl("rect", { x: -2, y: -2, width: 4, height: 4, fill: "#455a64", stroke: isSelected ? "#ff9800" : "#263238", "stroke-width": 1 }));
    g.appendChild(svgEl("line", { x1: 0, y1: -2, x2: 0, y2: -size - 2, stroke: "#455a64", "stroke-width": 1.5 }));
    for (let i = 1; i <= 3; i++) { g.appendChild(svgEl("path", { d: `M ${-i*3},${-size-2+i*2} Q ${0},${-size-6+i*2} ${i*3},${-size-2+i*2}`, fill: "none", stroke: "#42a5f5", "stroke-width": 1, opacity: 1 - i * 0.2 })); }
    weatherStationLayer().appendChild(g);
    const lbl = svgEl("text", { x: pos.x, y: pos.y + size + 4, class: "weather-station-label" }); lbl.textContent = ws.name ?? ""; weatherStationLayer().appendChild(lbl);
  });
}

let _wsListDelegated = false;
export function renderWeatherStationList({ selectWeatherStation, renderAll }: { selectWeatherStation: (id: number) => void; renderAll: () => void }) {
  const list = $("#weatherStationList"); if (!list) return; const refocus = keepRowFocus(list); list.innerHTML = "";
  if (state.weatherStations.length === 0) { list.innerHTML = `<div class="empty-state">${t("emptyStates.weatherStations")}</div>`; }
  state.weatherStations.forEach((ws) => {
    const station = ((getWeatherStations() || []) as Record<string, string>[]).find(s => s.id === ws.model), modelName = station ? station.name : ws.model;
    const item = document.createElement("div"); item.className = `weather-station-item${state.selectedWeatherStation === ws.id ? " selected" : ""}`;
    item.innerHTML = `<button type="button" class="list-item-select"${currentAttr(state.selectedWeatherStation === ws.id)}><span class="name">${escapeHtml(ws.name)}</span><span class="coords">${escapeHtml(modelName)}</span></button><button class="delete-btn" data-delete-ws="${escapeHtml(String(ws.id))}" title="${t("sidebar.deleteBtn")}">&times;</button>`;
    item.dataset.wsId = String(ws.id); list.appendChild(item);
  });
  refocus();
  if (!_wsListDelegated) { _wsListDelegated = true;
    list.addEventListener("click", (e: MouseEvent) => { const deleteBtn = (e.target as HTMLElement).closest(".delete-btn") as HTMLElement; if (deleteBtn) { const id = parseInt(deleteBtn.dataset.deleteWs!); saveState(); state.weatherStations = state.weatherStations.filter((w) => w.id !== id); if (state.selectedWeatherStation === id) state.selectedWeatherStation = null; renderAll(); return; } const item = (e.target as HTMLElement).closest(".weather-station-item") as HTMLElement; if (item) selectWeatherStation(parseInt(item.dataset.wsId!)); }); }
  const countEl = $("#weatherStationCount"); if (countEl) countEl.textContent = `(${state.weatherStations.length})`;
  const panelList = $("#panelWeatherStationList"); if (panelList) panelList.hidden = state.weatherStations.length === 0 && !state.selectedWeatherStation;
}

export function updateWeatherStationPropertiesPanel() {
  const panel = $("#panelWeatherStationProps"); if (!panel) return;
  const ws = state.weatherStations.find((w) => w.id === state.selectedWeatherStation);
  if (!ws) { panel.hidden = true; return; }
  panel.hidden = false; $("#wsName").value = ws.name; showField($("#wsX"), ws.x, "length", 1); showField($("#wsY"), ws.y, "length", 1);
  buildWeatherStationModelSelect($("#wsModel"), ws.model);
}

// ── Sprinklers ────────────────────────────────────────────────────
export function renderSprinklers() {
  clearLayer(sprinklerLayer()); clearLayer(labelLayer());

  // Coordinate captions sit centred under each head, so on a dense plan
  // they run into each other and the row turns into an unreadable smear
  // ("MP1000 (2.0, 2.0MP1000 (4.0, 2.0)"). Keep a running list of the
  // boxes already drawn and drop any caption that would collide — the
  // head keeps its name label, and selecting it still shows the full
  // detail in the properties panel.
  const captionBoxes: { x1: number; x2: number; y: number }[] = [];
  const CAPTION_CHAR_PX = 5.4;   // 9px monospace
  const CAPTION_ROW_PX = 10;     // treat captions within this band as one row
  const CAPTION_GAP_PX = 4;      // breathing room between neighbours

  const captionFits = (cx: number, cy: number, text: string): boolean => {
    const half = (text.length * CAPTION_CHAR_PX) / 2 + CAPTION_GAP_PX;
    const box = { x1: cx - half, x2: cx + half, y: cy };
    const clash = captionBoxes.some(
      (b) => Math.abs(b.y - box.y) < CAPTION_ROW_PX && box.x1 < b.x2 && box.x2 > b.x1,
    );
    if (clash) return false;
    captionBoxes.push(box);
    return true;
  };

  state.sprinklers.forEach((sp) => {
    const pos = metersToSvg(sp.x, sp.y), isSelected = isSel('sprinkler', sp.id, state.selectedSprinkler), preset = getPreset(sp.model);
    const g = svgEl("g", { class: `sprinkler-marker${isSelected ? " selected" : ""}`, "data-type": "sprinkler", "data-id": sp.id, transform: `translate(${pos.x}, ${pos.y})` });
    if (sp.zoneId) { const zone = state.zones.find((z) => z.id === sp.zoneId); if (zone) { const zs = getZoneStatus(zone); const haloColors = { ok: "#4caf50", warning: "#ff9800", "over-capacity": "#d32f2f", "no-valve": "#9c27b0" }; g.appendChild(svgEl("circle", { cx: 0, cy: 0, r: 10, fill: "none", stroke: haloColors[zs.status] || "#9c27b0", "stroke-width": 2, opacity: 0.5 })); } }
    if (preset.shape === "strip") { g.appendChild(svgEl("rect", { x: -8, y: -4, width: 16, height: 8, rx: 2, class: "body", fill: isSelected ? "#d32f2f" : preset.color, stroke: "#fff", "stroke-width": 2, transform: `rotate(${sp.rotation || 0})` })); }
    else { g.appendChild(svgEl("circle", { cx: 0, cy: 0, r: 6, class: "body", fill: isSelected ? "#d32f2f" : preset.color, stroke: "#fff", "stroke-width": 2 })); }
    if (!preset.shape && sp.arc !== 360) { const rotation = sp.rotation || 0, dirRad = (rotation - 90) * Math.PI / 180; g.appendChild(svgEl("line", { x1: 0, y1: 0, x2: Math.cos(dirRad) * 10, y2: Math.sin(dirRad) * 10, stroke: "#fff", "stroke-width": 2 })); }
    sprinklerLayer().appendChild(g);
    const nameLabel = svgEl("text", { x: pos.x, y: pos.y - 14, class: "sprinkler-label" }); nameLabel.textContent = sp.name ?? ""; labelLayer().appendChild(nameLabel);
    const at = `(${convertLength(sp.x, state.userUnits).toFixed(1)}, ${convertLength(sp.y, state.userUnits).toFixed(1)})`;
    const coordText = preset.shape === "strip" ? `${sp.model} ${formatLength(preset.width || 0, state.userUnits)} \u00D7 ${formatLength(preset.length || 0, state.userUnits)} ${at}` : `${sp.model} ${at}`;
    // The selected head always shows its caption — that is the one the user
    // is asking about, so it wins any collision.
    if (isSelected || captionFits(pos.x, pos.y + 20, coordText)) {
      const coordLabel = svgEl("text", { x: pos.x, y: pos.y + 20, class: "sprinkler-coords" });
      coordLabel.textContent = coordText;
      labelLayer().appendChild(coordLabel);
    }
  });
}

// ── Temp Drawing ──────────────────────────────────────────────────
export function renderTemp() {
  clearLayer(tempLayer());
  if (state.mode === "boundary" && state.tempBoundary.length > 0) {
    const points = state.tempBoundary.map((p) => { const s = metersToSvg(p.x, p.y); return `${s.x},${s.y}`; }).join(" ");
    if (state.tempBoundary.length >= 3) { tempLayer().appendChild(svgEl("polygon", { points, class: "temp-polygon" })); }
    else { tempLayer().appendChild(svgEl("polyline", { points, class: "temp-line", fill: "none" })); }
    state.tempBoundary.forEach((p) => { const s = metersToSvg(p.x, p.y); tempLayer().appendChild(svgEl("circle", { cx: s.x, cy: s.y, r: 4, fill: "#4caf50", stroke: "#fff", "stroke-width": 2 })); });
  }
  if (state.mode === "pipe" && state.tempPipe.length > 0) {
    const pts = state.tempPipe.map((p) => { const s = metersToSvg(p.x, p.y); return `${s.x},${s.y}`; }).join(" ");
    tempLayer().appendChild(svgEl("polyline", { points: pts, fill: "none", stroke: "#546e7a", "stroke-width": 2, "stroke-dasharray": "6 3" }));
    state.tempPipe.forEach((p) => { const s = metersToSvg(p.x, p.y); tempLayer().appendChild(svgEl("circle", { cx: s.x, cy: s.y, r: 4, fill: "#546e7a", stroke: "#fff", "stroke-width": 2 })); });
  }
  if (state.mode === "drip" && state.tempDripLine.length > 0) {
    const dripType = ((getDripTypes() || {}) as Record<string, Record<string, unknown>>)[state.activeDripModel] || {}, color = (dripType.color as string) || "#795548";
    const pts = state.tempDripLine.map((p) => { const s = metersToSvg(p.x, p.y); return `${s.x},${s.y}`; }).join(" ");
    tempLayer().appendChild(svgEl("polyline", { points: pts, fill: "none", stroke: color, "stroke-width": 2.5, "stroke-dasharray": "6 4" }));
    state.tempDripLine.forEach((p) => { const s = metersToSvg(p.x, p.y); tempLayer().appendChild(svgEl("circle", { cx: s.x, cy: s.y, r: 4, fill: color, stroke: "#fff", "stroke-width": 2 })); });
  }
  if (state.mode === "zone" && state.tempZone.length > 0) {
    const points = state.tempZone.map((p) => { const s = metersToSvg(p.x, p.y); return `${s.x},${s.y}`; }).join(" ");
    tempLayer().appendChild(svgEl("polyline", { points, fill: "none", stroke: "#9c27b0", "stroke-width": 1.5, "stroke-dasharray": "6 3" }));
    state.tempZone.forEach((p) => { const s = metersToSvg(p.x, p.y); tempLayer().appendChild(svgEl("circle", { cx: s.x, cy: s.y, r: 4, fill: "#9c27b0", stroke: "#fff", "stroke-width": 2 })); });
  }
}

// ── Sprinkler List ────────────────────────────────────────────────
let _sprinklerListDelegated = false;
export function renderSprinklerList({ selectSprinkler, renderAll }: { selectSprinkler: (id: number) => void; renderAll: () => void }) {
  const list = $("#sprinklerList"); const refocus = keepRowFocus(list); list.innerHTML = "";
  if (state.sprinklers.length === 0) { list.innerHTML = `<div class="empty-state">${t("emptyStates.sprinklers")}</div>`; }
  state.sprinklers.forEach((sp) => {
    const preset = getPreset(sp.model), item = document.createElement("div");
    item.className = `sprinkler-item${state.selectedSprinkler === sp.id ? " selected" : ""}`;
    const zoneName = sp.zoneId ? (state.zones.find((z) => z.id === sp.zoneId)?.name || "") : "";
    // The catalog colour lands in attribute position while every sibling
    // field on this line goes through escapeHtml — a colour carrying a
    // quote would break out of the style attribute. Drop the declaration
    // entirely when the value isn't a recognised CSS colour.
    const safeColor = sanitizeColor(preset.color);
    const colorStyle = safeColor ? ` style="color:${safeColor}"` : "";
    const badgeStyle = safeColor ? ` style="background:${safeColor}"` : "";
    const zoneBadge = zoneName ? `<span class="zone-badge"${badgeStyle}>${escapeHtml(zoneName)}</span>` : "";
    item.innerHTML = `<button type="button" class="list-item-select"${currentAttr(state.selectedSprinkler === sp.id)}><span class="name"${colorStyle}>${escapeHtml(sp.name)}</span>${zoneBadge}<span class="coords">${escapeHtml(sp.model)} ${sp.arc}\u00B0 r=${formatRadius(sp.radius || 0, state.userUnits)}</span></button><button class="delete-btn" data-delete-id="${escapeHtml(String(sp.id))}" title="${t("sidebar.deleteBtn")}">&times;</button>`;
    item.dataset.sprinklerId = String(sp.id); list.appendChild(item);
  });
  refocus();
  if (!_sprinklerListDelegated) { _sprinklerListDelegated = true;
    list.addEventListener("click", (e: MouseEvent) => { const deleteBtn = (e.target as HTMLElement).closest(".delete-btn") as HTMLElement; if (deleteBtn) { const id = parseInt(deleteBtn.dataset.deleteId!); saveState(); state.sprinklers = state.sprinklers.filter((s) => s.id !== id); if (state.selectedSprinkler === id) state.selectedSprinkler = null; renderAll(); return; } const item = (e.target as HTMLElement).closest(".sprinkler-item") as HTMLElement; if (item) selectSprinkler(parseInt(item.dataset.sprinklerId!)); }); }
  $("#sprinklerCount").textContent = `(${state.sprinklers.length})`; $("#statSprinklers").textContent = state.sprinklers.length;
  $("#panelSprinklerList").hidden = state.sprinklers.length === 0 && !state.selectedSprinkler;
}

// ── Zone List ────────────────────────────────────────────────────
let _zoneListDelegated = false;
export function renderZoneList(renderAll: () => void) {
  const list = $("#zoneList"); const refocus = keepRowFocus(list); list.innerHTML = "";
  if (state.zones.length === 0) { list.innerHTML = `<div class="empty-state">${t("emptyStates.zones")}</div>`; }
  state.zones.forEach((zone) => {
    const status = getZoneStatus(zone), valve = zone.valveModel ? ((getValves() || {}) as Record<string, Record<string, string>>)[zone.valveModel] : null;
    const item = document.createElement("div"); item.className = `zone-item${state.selectedZone === zone.id ? " selected" : ""}`;
    const zoneFlowLabel = formatFlow(status.flow, state.userUnits);
    const zoneMaxFlowLabel = status.maxFlow ? formatFlow(status.maxFlow, state.userUnits) : "\u221E";
    item.innerHTML = `<button type="button" class="zone-item-main list-item-select"${currentAttr(state.selectedZone === zone.id)}><span class="zone-status-dot ${status.status}"></span><span class="zone-item-text"><span class="zone-item-name">${escapeHtml(zone.name)}</span><span class="zone-item-meta">${valve ? escapeHtml(valve.size) : "\u2014"} | ${status.count} | ${zoneFlowLabel} / ${zoneMaxFlowLabel}</span></span></button><button class="delete-btn" data-delete-zone="${escapeHtml(String(zone.id))}" title="${t("sidebar.deleteBtn")}">&times;</button>`;
    item.dataset.zoneId = String(zone.id); list.appendChild(item);
  });
  refocus();
  if (!_zoneListDelegated) { _zoneListDelegated = true;
    list.addEventListener("click", (e: MouseEvent) => { const deleteBtn = (e.target as HTMLElement).closest(".delete-btn") as HTMLElement; if (deleteBtn) { const id = parseInt(deleteBtn.dataset.deleteZone!); saveState(); state.zones = state.zones.filter((z) => z.id !== id); if (state.selectedZone === id) state.selectedZone = null; autoAssignSprinklersToZones(); renderAll(); return; } const item = (e.target as HTMLElement).closest(".zone-item") as HTMLElement; if (item) { clearSelection(); state.selectedZone = parseInt(item.dataset.zoneId!); renderAll(); } }); }
  $("#zoneCount").textContent = `(${state.zones.length})`; $("#panelZoneList").hidden = state.zones.length === 0 && !state.selectedZone;
}

// ── Stats ─────────────────────────────────────────────────────────
export function updateStats() {
  let area = 0;
  if (state.boundary.length >= 3) { const pts = state.boundary; for (let i = 0; i < pts.length; i++) { const j = (i + 1) % pts.length; area += pts[i].x * pts[j].y; area -= pts[j].x * pts[i].y; } area = Math.abs(area) / 2; }
  else { area = state.plotWidth * state.plotHeight; }
  $("#statArea").textContent = formatArea(area, state.userUnits);
  if (state.sprinklers.length > 0 && area > 0) {
    let coveredArea = 0;
    state.sprinklers.forEach((sp) => { const p = getPreset(sp.model); if (p.shape === "strip") { coveredArea += p.width! * p.length!; } else { coveredArea += Math.PI * sp.radius! * sp.radius! * (sp.arc / 360); } });
    const pct = (coveredArea / area) * 100;
    $("#statCoverage").textContent = pct > 100 ? "100%+" : `~${pct.toFixed(0)}%`;
  } else { $("#statCoverage").textContent = "0%"; }
  let totalFlow = 0;
  state.sprinklers.forEach((sp) => { totalFlow += getArcData(sp.model, sp.arc).flow; });
  $("#statFlow").textContent = `${formatFlow(totalFlow, state.userUnits)} (${formatFlowM3h(totalFlow * 60 / 1000, state.userUnits)})`;

  updatePlotDimensionReadouts();
}

/** The DRAW flyout shows the plot size as read-only text. Nothing ever wrote
 *  to it, so it sat at an em dash even with a boundary on the canvas. Report
 *  the drawn outline's extents where there is one — that is the number the
 *  user is checking while tracing — and fall back to the canvas size
 *  otherwise. */
function updatePlotDimensionReadouts() {
  const widthEl = document.getElementById("plotWidthReadout");
  const heightEl = document.getElementById("plotHeightReadout");
  if (!widthEl && !heightEl) return;

  let w = state.plotWidth;
  let h = state.plotHeight;
  if (state.boundary.length >= 2) {
    const xs = state.boundary.map((p) => p.x);
    const ys = state.boundary.map((p) => p.y);
    w = Math.max(...xs) - Math.min(...xs);
    h = Math.max(...ys) - Math.min(...ys);
  }

  if (widthEl) widthEl.textContent = formatLength(w, state.userUnits);
  if (heightEl) heightEl.textContent = formatLength(h, state.userUnits);
}

// ── Smart Guides Rendering ────────────────────────────────────────
export function renderSmartGuides(point: { x: number; y: number }, exclude: SmartGuideExclusion) {
  clearLayer(smartGuideLayer());
  if (!state.showGuides) return;
  const guides = findSmartGuides(point, exclude), vb = state.viewBox;
  for (const g of guides) {
    if (g.axis === "x") {
      const sx = metersToSvg(g.position, 0).x;
      smartGuideLayer().appendChild(svgEl("line", { x1: sx, y1: vb.y, x2: sx, y2: vb.y + vb.h, class: "smart-guide-line" }));
      if (g.from) { const mid = metersToSvg(g.position, (g.from.y + point.y) / 2); const lbl = svgEl("text", { x: mid.x + 8, y: mid.y, class: "smart-guide-label" }); lbl.textContent = formatLength(g.dist, state.userUnits); smartGuideLayer().appendChild(lbl); }
    }
    if (g.axis === "y") {
      const sy = metersToSvg(0, g.position).y;
      smartGuideLayer().appendChild(svgEl("line", { x1: vb.x, y1: sy, x2: vb.x + vb.w, y2: sy, class: "smart-guide-line" }));
      if (g.from) { const mid = metersToSvg((g.from.x + point.x) / 2, g.position); const lbl = svgEl("text", { x: mid.x, y: mid.y - 6, class: "smart-guide-label" }); lbl.textContent = formatLength(g.dist, state.userUnits); smartGuideLayer().appendChild(lbl); }
    }
  }
}

export function renderManualGuides() {
  clearLayer(guideLayer());
  if (!state.showGuides) return;
  const vb = state.viewBox;
  state.guides.forEach((g) => {
    if (g.axis === "x") { const sx = metersToSvg(g.position, 0).x; guideLayer().appendChild(svgEl("line", { x1: sx, y1: vb.y, x2: sx, y2: vb.y + vb.h, class: "guide-line" })); const lbl = svgEl("text", { x: sx + 4, y: vb.y + 14, class: "guide-label" }); lbl.textContent = `x=${formatLength(g.position, state.userUnits)}`; guideLayer().appendChild(lbl); }
    else { const sy = metersToSvg(0, g.position).y; guideLayer().appendChild(svgEl("line", { x1: vb.x, y1: sy, x2: vb.x + vb.w, y2: sy, class: "guide-line" })); const lbl = svgEl("text", { x: vb.x + 4, y: sy - 4, class: "guide-label" }); lbl.textContent = `y=${formatLength(g.position, state.userUnits)}`; guideLayer().appendChild(lbl); }
  });
}

export function addGuide(axis: "x" | "y", position: number) {
  state.guides.push({ id: state.nextGuideId++, axis, position });
  renderManualGuides();
}

// ── Zone Properties ──────────────────────────────────────────────
export function updateZonePropertiesPanel({ selectSprinkler }: { selectSprinkler: (id: number) => void }) {
  const panel = $("#panelZoneProps"), zone = state.zones.find((z) => z.id === state.selectedZone);
  if (!zone) { panel.hidden = true; return; }
  panel.hidden = false; $("#zoneName").value = zone.name; buildValveSelect($("#zoneValve"), zone.valveModel);
  const status = getZoneStatus(zone);
  $("#zoneSpCount").textContent = status.count; $("#zoneFlow").textContent = formatFlow(status.flow, state.userUnits);
  $("#zoneMaxFlow").textContent = status.maxFlow ? formatFlow(status.maxFlow, state.userUnits) : "\u2014";
  const warning = $("#zoneWarning");
  const flows = { flow: convertFlow(status.flow, state.userUnits).toFixed(1), maxFlow: Number(convertFlow(status.maxFlow ?? 0, state.userUnits).toFixed(1)), unit: getUnitLabels(state.userUnits).flow };
  if (status.status === "over-capacity") { warning.hidden = false; warning.className = "zone-warning"; warning.textContent = t("warnings.flowExceeds", flows); }
  else if (status.status === "warning") { warning.hidden = false; warning.className = "zone-warning warning-yellow"; warning.textContent = t("warnings.flowNearLimit", flows); }
  else { warning.hidden = true; }
  const spList = $("#zoneSprinklerList"); spList.innerHTML = "";
  getZoneSprinklers(zone).forEach((sp) => {
    const arcData = getArcData(sp.model, sp.arc), item = document.createElement("button"); item.type = "button"; item.className = "zone-sp-item list-item-select";
    item.innerHTML = `<span>${escapeHtml(sp.name)} \u2014 ${escapeHtml(sp.model)} ${sp.arc}\u00B0</span><span class="flow">${formatFlow(arcData.flow, state.userUnits)}</span>`;
    item.addEventListener("click", () => { selectSprinkler(sp.id); }); spList.appendChild(item);
  });
}

// ── Sprinkler Properties ──────────────────────────────────────────
export function updatePropertiesPanel() {
  const panel = $("#panelSprinklerProps"), sp = state.sprinklers.find((s) => s.id === state.selectedSprinkler);
  if (!sp) { panel.hidden = true; return; }
  panel.hidden = false;
  const preset = getPreset(sp.model), arcData = getArcData(sp.model, sp.arc), series = getSeriesForModel(sp.model);
  buildModelSelect($("#sprinklerModel"), sp.model); $("#sprinklerName").value = sp.name;
  populateArcSelect($("#sprinklerArc"), sp.model, sp.arc);
  showField($("#sprinklerX"), sp.x, "length", 1); showField($("#sprinklerY"), sp.y, "length", 1);
  const radiusMin = toFieldText(preset.radiusMin ?? 0, "length", state.userUnits), radiusMax = toFieldText(preset.radiusMax ?? 0, "length", state.userUnits);
  showField($("#sprinklerRadius"), sp.radius ?? 0, "length"); $("#sprinklerRadius").min = radiusMin; $("#sprinklerRadius").max = radiusMax;
  $("#sprinklerRotation").value = sp.rotation || 0;
  $("#modelRange").textContent = t("warnings.range", { min: radiusMin, max: radiusMax, unit: getUnitLabels(state.userUnits).length });
  buildZoneSelect($("#sprinklerZone"), sp.zoneId); buildBodySelect($("#sprinklerBody"), sp.model, sp.bodyModel || "");
  const flowLabel = formatFlow(arcData.flow, state.userUnits);
  const precipLabel = arcData.precip ? formatPrecipRate(arcData.precip, state.userUnits) : "\u2014";
  // A series whose spec sheet has not been captured has no nominal pressure.
  // Mirror the precip-rate dash rather than rendering "NaN bar".
  const pressureLabel = Number.isFinite(Number(series.pressure))
    ? formatPressure(Number(series.pressure), state.userUnits)
    : "—";
  $("#presetInfo").innerHTML = `<div class="info-row"><span class="info-label">${t("presetInfo.flow")}</span><span class="info-value">${flowLabel}</span></div><div class="info-row"><span class="info-label">${t("presetInfo.precipRate")}</span><span class="info-value">${precipLabel}</span></div><div class="info-row"><span class="info-label">${t("presetInfo.pressure")}</span><span class="info-value">${pressureLabel}</span></div>`;
}
